import React, { useRef, useState } from "react";
import {
  Toast,
  Modal,
  Form,
  Button,
  Spin,
  Typography,
} from "@douyinfe/semi-ui";
import type {
  BaseFormApi as FormApi,
  FormState,
} from "@douyinfe/semi-foundation/lib/es/form/interface";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@state/store";
import { useTranslation, Trans } from "react-i18next";
import { setLocalStorage, removeLocalStorage } from "@utils/localstorage";
import { LoginEnum } from "@services/account/enum/login.enum";
import { useNavigate } from "react-router-dom";
import {
  LoginJobPayloadModel,
  TokenInfoModel,
} from "@services/home/model/home.model";
import { encrypt } from "@utils/encryption.util";
import { useCountDown } from "ahooks";
import { setToken, removeToken } from "@state/global/reducer";
import { LoginPayloadModel } from "@services/account/model/account.model";
import {
  loginApi,
  checkNeedOrgnizationApi,
} from "@services/account/account.api";
import {
  asyncEmailValidate,
  codeValidate,
  organizationValidate,
} from "@utils/validator";

import styles from "./index.module.scss";

const { Text } = Typography;

interface Prop {
  type: string;
  tokenInfo?: TokenInfoModel;
  username?: string;
  loading?: boolean;
  submitingJob?: boolean;
  cancel: () => void;
  submit: (job_name?: string) => void;
  logined?: () => void;
}

const JobLoginDialog: React.FC<Prop> = ({
  type,
  tokenInfo,
  username,
  loading,
  submitingJob,
  cancel,
  submit,
  logined,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const formRef = useRef<FormApi<LoginJobPayloadModel>>();
  const [isSendEmail, setIsSendEmail] = useState(false);
  const [emailDate, setEmailDate] = useState<number>();
  const [submiting, setSubmiting] = useState(false);

  const [step, setStep] = useState(type === "anonymous" ? 1 : 2);
  const [emailText, setTextEmail] = useState<string>();

  const [showOrgnization, setShowOrgnization] = useState(false);

  const [emailCountdown] = useCountDown({
    targetDate: emailDate,
    onEnd: () => {},
  });
  function handleCancel() {
    cancel();
  }

  async function onSubmit() {
    if (!formRef.current) {
      return;
    }

    try {
      const values = await formRef.current.validate();

      if (step === 1) {
        if (values.email && values.code) {
          const params: LoginPayloadModel = {
            email: values.email.trim(),
            code: values.code.trim(),
            login_source: "geo_flow_server",
          };

          if (values.organization) {
            params.geo_flow_org_name = values.organization.trim();
          }

          setSubmiting(true);
          const { code, data, message } = await loginApi(params);
          if (code === 200) {
            setLocalStorage(LoginEnum.Token, data);
            dispatch(setToken(data));
            setStep(2);
            Toast.success({
              content: message,
              showClose: false,
            });

            if (logined) {
              logined();
            }
            setTextEmail(values.email);
          }
          setSubmiting(false);
        }
      } else if (step === 2) {
        submit(values.job_name);
      }
    } catch (e) {
      setSubmiting(false);
    }
  }

  function handleSubmit() {
    onSubmit();
  }

  async function sendEmail(email: string) {
    try {
      setIsSendEmail(true);
      const { code, data, message } = await checkNeedOrgnizationApi(
        encrypt(email)
      );

      setIsSendEmail(false);
      if (code === 200) {
        setEmailDate(Date.now() + 60000);
        Toast.success(message);
      }

      if (code === 200 && data.show_org_collect) {
        setShowOrgnization(true);
      }
    } catch (e) {
      setIsSendEmail(false);
    }
  }

  function sendEmailCode() {
    const email = formRef.current?.getValue("email");
    if (email) {
      sendEmail(email);
    }
  }

  function logOut() {
    removeLocalStorage(LoginEnum.Token);
    dispatch(removeToken());
    cancel();
    Toast.success("Log out success");
  }

  function toTerm() {
    window.open("/terms", "_blank");
  }

  function toPromotion() {
    window.open("/promotion", "_blank");
  }

  return (
    <Modal
      title={
        type === "anonymous" && step === 1
          ? t("jobLoginModal.message.notLogin")
          : t("jobLoginModal.message.logined")
      }
      visible={true}
      onCancel={handleCancel}
      closeOnEsc={true}
      width="420px"
      centered
      cancelText={t("jobLoginModal.operation.cancel")}
      okText={
        step === 1
          ? t("jobLoginModal.operation.continue")
          : t("jobLoginModal.operation.ok")
      }
      onOk={handleSubmit}
      confirmLoading={submiting || submitingJob}
    >
      <Form
        autoComplete="off"
        getFormApi={(formApi) => (formRef.current = formApi)}
      >
        {({ formState }: { formState: FormState<LoginJobPayloadModel> }) => (
          <>
            {(type === "registered" || step === 2) && (
              <Form.Input
                label={{
                  text: t("jobLoginModal.jobName"),
                  required: false,
                }}
                rules={[{ required: true, message: "Required error" }]}
                field="job_name"
                placeholder={t("jobLoginModal.jobPlaceholder")}
              />
            )}

            {type === "anonymous" && step === 1 ? (
              <>
                <Form.Input
                  label={{
                    text: t("loginModal.emailLabel"),
                    required: false,
                  }}
                  validate={asyncEmailValidate}
                  field="email"
                  trigger="blur"
                  placeholder={t("loginModal.emailPlaceholder")}
                />
                <Form.Input
                  label={{
                    text: t("loginModal.verificationLabel"),
                    required: false,
                  }}
                  validate={codeValidate}
                  trigger="blur"
                  field="code"
                  className={styles.codeWrapper}
                  addonAfter={
                    <Button
                      className={styles.sendBtn}
                      theme="borderless"
                      style={{
                        boxShadow: "none",
                        height: "30px",
                        borderRadius: "0 4px 4px 0",
                      }}
                      disabled={
                        isSendEmail ||
                        !formState.touched?.email ||
                        emailCountdown !== 0 ||
                        !!formState.errors?.email
                      }
                      onClick={sendEmailCode}
                      size="small"
                    >
                      {emailCountdown !== 0
                        ? `${t("loginModal.resend")} (${Math.round(
                            emailCountdown / 1000
                          )}s)`
                        : t("loginModal.send")}
                    </Button>
                  }
                  placeholder={t("loginModal.verificationPlaceholder")}
                />

                {showOrgnization && (
                  <Form.Input
                    label={{
                      text: t("loginModal.organizationLabel"),
                      required: false,
                    }}
                    validate={organizationValidate}
                    trigger="blur"
                    field="organization"
                    placeholder={t("loginModal.organizationPlaceholder")}
                  />
                )}
              </>
            ) : (
              <>
                <div className={styles.logoutContainer}>
                  {t("jobLoginModal.message.loginText")}&nbsp;
                  <Text
                    ellipsis={{ showTooltip: true }}
                    style={{
                      maxWidth: 160,
                      fontSize: "12px",
                      color: "var(--semi-color-text-2)",
                    }}
                  >
                    {username || emailText || "-"}
                  </Text>
                  .&nbsp;
                  <span className={styles.link} onClick={logOut}>
                    {t("jobLoginModal.operation.logout")}
                  </span>
                  .
                </div>

                <div className={styles.text} style={{ marginBottom: "8px" }}>
                  {!loading && tokenInfo && (
                    <div>
                      Compute:{" "}
                      <span className={styles.bold}>{tokenInfo.token}</span>{" "}
                      tokens ×{" "}
                      <span className={styles.bold}>
                        {tokenInfo.token_times}
                      </span>{" "}
                      ={" "}
                      <span className={styles.bold}>
                        {tokenInfo.compute_cost}
                      </span>
                      ; Balance:{" "}
                      <span
                        className={styles.bold}
                        style={{
                          color:
                            tokenInfo.compute_remain < tokenInfo.compute_cost
                              ? "red"
                              : "",
                        }}
                      >
                        {tokenInfo.compute_remain}
                      </span>
                      .
                      <br />
                      Compute limits refresh each day.
                    </div>
                  )}

                  {loading && (
                    <div>
                      Computing #tokens <Spin size="small" />
                    </div>
                  )}

                  {/* <div>
                    Free users can only generate 10 samples/job. Want
                    larger-scale designs with advanced constraints and scoring?{" "}
                    <span className={styles.link} onClick={toPromotion}>
                      Upgrade your plan
                    </span>
                    .
                  </div> */}
                </div>

                <div className={styles.text}>
                  <Trans
                    i18nKey="jobLoginModal.message.terms"
                    components={[
                      <span
                        className={styles.link}
                        onClick={toTerm}
                        key={"link"}
                      />,
                    ]}
                  />
                </div>
              </>
            )}
          </>
        )}
      </Form>
    </Modal>
  );
};

export default JobLoginDialog;
